
import React from "react";
import { CaseStudyLayout } from "@/components/case-studies/CaseStudyLayout";
import { ProcessStep } from "@/components/case-studies/ProcessStep";
import { CaseMetrics } from "@/components/case-studies/CaseMetrics";
import { DollarSign, MessageSquare, Phone, Users, Zap } from "lucide-react";

const B2BLending = () => {
  const metrics = [
    {
      value: "200+",
      label: "Leads/Month",
      description: "Generated consistently month after month"
    },
    {
      value: "$24",
      label: "Cost Per Lead",
      description: "Industry average: $350"
    },
    {
      value: "85%",
      label: "Connect Rate",
      description: "Industry standard: 10-30%"
    },
    {
      value: "$20k-$600k",
      label: "Loan Range",
      description: "Successfully funded through our system"
    },
    {
      value: "14x",
      label: "ROI Improvement",
      description: "Compared to industry standard acquisition"
    },
    {
      value: "48hrs",
      label: "Implementation Time",
      description: "From kickoff to first leads"
    }
  ];

  return (
    <CaseStudyLayout
      title="Revolutionizing B2B Lending Lead Generation"
      industry="B2B Lending"
    >
      <div className="max-w-4xl mx-auto mb-16">
        <p className="text-xl leading-relaxed mb-8 fade-in">
          For our B2B lending client, we created a comprehensive lead generation system 
          that produced over 200 qualified leads monthly at an average cost of just $24 per lead—a 
          fraction of the industry average of $350. Our end-to-end solution streamlined the entire 
          process from acquisition to qualification to human handoff.
        </p>
        
        <CaseMetrics metrics={metrics} />
        
        <div className="my-16">
          <h2 className="mb-10 fade-in">Our Process</h2>
          
          <ProcessStep
            number="01"
            title="Strategic Lead Generation"
            description="We implemented targeted Meta advertising campaigns specifically designed to reach businesses seeking funding. By using precise audience targeting and compelling ad creative, we were able to generate high-quality leads at a significantly lower cost than industry averages."
            image="/lead-generation.webp"
            icon={<Users className="h-6 w-6" />}
          />
          
          <ProcessStep
            number="02"
            title="Custom Landing Page Development"
            description="We created high-converting landing pages with optimized form fields that captured essential qualifying information. These pages were designed to maximize conversion rates while gathering crucial data points needed for the qualification process."
            image="/landing-page.webp"
            icon={<Zap className="h-6 w-6" />}
            reversed
          />
          
          <ProcessStep
            number="03"
            title="SMS Permission Acquisition"
            description="After form submission, we implemented an automated SMS system to quickly engage with leads and obtain permission to call them. This created a warm introduction and significantly increased the likelihood of a successful connection later in the process."
            image="/sms-permission.webp"
            icon={<MessageSquare className="h-6 w-6" />}
          />
          
          <ProcessStep
            number="04"
            title="Instant AI-Powered Calling"
            description="Once permission was granted, our Vail AI system immediately called the leads. Vail conducted preliminary qualification, gathered additional information, and assessed their funding needs and timeline—all while maintaining a natural, engaging conversation."
            image="/ai-calling.webp"
            icon={<Phone className="h-6 w-6" />}
            reversed
          />
          
          <ProcessStep
            number="05"
            title="Qualified Lead Handoff"
            description="For leads that met the qualification criteria, Vail seamlessly transferred them to human loan specialists. Because the leads were pre-screened and warmed up, we achieved an 85% connect rate, vastly outperforming the industry standard of 10-30% for cold acquisition."
            image="/lead-handoff.webp"
            icon={<DollarSign className="h-6 w-6" />}
          />
        </div>
        
        <div className="bg-secondary/30 p-8 rounded-2xl my-16 fade-in">
          <h3 className="mb-4">The Results</h3>
          <p className="mb-4">
            This comprehensive system enabled our client to successfully fund loans 
            ranging from $20,000 to $600,000. By combining strategic advertising, 
            permission-based outreach, and AI-powered qualification, we created a 
            scalable, efficient lead generation machine that dramatically outperformed 
            traditional methods.
          </p>
          <p>
            The system not only reduced cost per lead by over 93% compared to industry 
            averages but also improved the quality of leads and the efficiency of the 
            sales process, allowing the client to focus their human resources on closing 
            deals rather than prospecting.
          </p>
        </div>
      </div>
    </CaseStudyLayout>
  );
};

export default B2BLending;
